/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailOrganize.js
 * @description
 * Pure helpers for the mail "Organize" feature (folder / colour mark / AI category / labels):
 * the colour palette (ids come from the backend `mailColors` constant, hex values are the app's
 * own themed shades), label-id resolution for list rows and normalisation of the
 * `GET /mail/organize/summary` payload. No React, no network - unit-testable.
 */

/** Palette ids accepted by the backend (`src/api/constants/mailColors.js`), in display order. */
export const MAIL_COLOR_IDS = ['red', 'orange', 'amber', 'green', 'teal', 'blue', 'violet', 'pink', 'gray'];

const COLOR_LIGHT = {
    red: '#DC2626', orange: '#EA580C', amber: '#D97706', green: '#16A34A', teal: '#0D9488',
    blue: '#2563EB', violet: '#7C3AED', pink: '#DB2777', gray: '#6B7280',
};
const COLOR_DARK = {
    red: '#F87171', orange: '#FB923C', amber: '#FBBF24', green: '#4ADE80', teal: '#2DD4BF',
    blue: '#60A5FA', violet: '#A78BFA', pink: '#F472B6', gray: '#9CA3AF',
};

/**
 * Themed hex of a palette id.
 * @param {string} [id] Palette id (case-insensitive).
 * @param {boolean} [isDark] Dark theme flag.
 * @returns {string} `#RRGGBB`, or `''` when the id is empty / unknown.
 */
export const colorHex = (id, isDark = false) => {
    const key = String(id || '').trim().toLowerCase();

    return (isDark ? COLOR_DARK : COLOR_LIGHT)[key] || '';
};

/**
 * Normalise the colour mark of a message row.
 * @param {Object} [message] Message document.
 * @returns {string} Palette id or `''`.
 */
export const messageColorId = (message) => {
    const key = String(message?.colorMark || '').trim().toLowerCase();

    return MAIL_COLOR_IDS.includes(key) ? key : '';
};

/**
 * Build an `id -> {id, name, color}` index from the mailbox label list.
 * @param {Array} [labels] `mail.labels` (settings labels) - entries may be `{id|_id, name, color}`.
 * @returns {Object} Index keyed by label id.
 */
export const buildLabelIndex = (labels) => {
    const out = {};
    (Array.isArray(labels) ? labels : []).forEach((l) => {
        const id = String(l?.id || l?._id || '');
        if (id) {
            out[id] = {id, name: l?.name || l?.label || '', color: l?.color || ''};
        }
    });

    return out;
};

/**
 * Resolve a row's label ids to displayable chips (unknown ids are dropped, never shown raw).
 * @param {Object} message Message document (`labels` = ids, or legacy `{name,color}` / strings).
 * @param {Object} [index] Result of {@link buildLabelIndex}.
 * @param {number} [max] Max chips returned.
 * @returns {{chips:Array<{label:string,color:string}>, extra:number}} Chips + overflow count.
 */
export const resolveRowLabels = (message, index = {}, max = 3) => {
    const list = Array.isArray(message?.labels) ? message.labels : [];
    const all = list.map((l) => {
        if (l && typeof l === 'object') {
            return {label: l.name || l.label || '', color: l.color || ''};
        }
        const hit = index[String(l)];

        return hit ? {label: hit.name, color: hit.color} : null;
    }).filter((l) => l && l.label);

    return {chips: all.slice(0, max), extra: Math.max(0, all.length - max)};
};

/**
 * Normalise `GET /mail/organize/summary` (every dimension defaults to an empty list).
 * @param {Object} [raw] Response body.
 * @returns {{folders:Array, colors:Array, categories:Array, labels:Array}} Safe summary.
 */
export const normalizeOrganizeSummary = (raw) => {
    const list = (v) => (Array.isArray(v) ? v.filter((x) => x && x.key !== undefined && x.key !== null) : []);

    return {
        folders: list(raw?.folders),
        colors: list(raw?.colors),
        categories: list(raw?.categories),
        labels: list(raw?.labels),
    };
};

/**
 * Build the list-query params of an organize filter (`color` / `category` / `label`).
 * @param {{color?:string, category?:string, label?:string}|null} filter Active filter.
 * @returns {Object} Params to merge into `GET /mail/messages` (empty when no filter).
 */
export const organizeFilterParams = (filter) => {
    const out = {};
    if (filter?.color) {
        out.color = filter.color;
    }
    if (filter?.category) {
        out.category = filter.category;
    }
    if (filter?.label) {
        out.label = filter.label;
    }

    return out;
};

/**
 * Build the `POST /mail/messages/organize` body from the sheet state. Only changed dimensions are sent.
 * @param {{ids:string[], folder?:string|null, addLabelIds?:string[], removeLabelIds?:string[], color?:string|null|undefined, applyToThread?:boolean}} input
 *        `color`: undefined = untouched, `null`/`''` = clear, id = set.
 * @returns {Object|null} Request body, or `null` when there is nothing to apply.
 */
export const buildOrganizePayload = ({ids, folder, addLabelIds = [], removeLabelIds = [], color, applyToThread}) => {
    const messageIds = (Array.isArray(ids) ? ids : []).filter(Boolean);
    if (!messageIds.length) {
        return null;
    }
    const body = {messageIds};
    if (folder) {
        body.folderId = folder;
    }
    if (addLabelIds.length) {
        body.addLabelIds = addLabelIds;
    }
    if (removeLabelIds.length) {
        body.removeLabelIds = removeLabelIds;
    }
    if (color !== undefined) {
        body.color = color || null;
    }
    if (applyToThread) {
        body.applyToThread = true;
    }

    return Object.keys(body).length > 1 && (body.folderId || body.addLabelIds || body.removeLabelIds || 'color' in body) ? body : null;
};
